import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HtmlTwoToneIcon from "@mui/icons-material/HtmlTwoTone";
import PictureAsPdfTwoToneIcon from "@mui/icons-material/PictureAsPdfTwoTone";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import { Card, CardActionArea, IconButton, Link, Paper, Stack, Typography } from "@mui/material";
import { userPermissionsSelector } from "../../store/slices/userSlice";
import { ERRORS_MESSAGE, SUCCESS_MESSAGE } from "../../constants";
import { applicationSelector, deleteDocument, userDetailsSelector } from "../../store/slices/applicationFormSlice";
import ConfirmModal from "../customComponents/ConfirmModal";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import colors from "../../style/colors";

const ext = [".jpg", ".jpeg", ".png", ".gif"];

const nameOfDocUploadComponent = [
  "privacyAgreement",
  "otherDocuments",
];

export default function Document({ document, name }) {
  const dispatch = useDispatch();
  const application = useSelector(applicationSelector);
  const { isDeletingDocument } = useSelector(userDetailsSelector);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const permissions = useSelector(userPermissionsSelector);
  const isDisable = !permissions?.application?.edit || !application?.canEditApplication;

  if (nameOfDocUploadComponent.includes(name) && !document.url) {
    return null;
  }

  const handle = {
    onDelete: (data) => {
      if (data?.isSave) {
        const payloadData = {
          ...(document?.customer && { customerId: document.customer }),
          ...(document?.entity && { entityId: document.entity }),
          ...(document?.asset && { assetId: document.asset }),
          documentId: document._id,
          applicationId: document.application,
        };
        dispatch(deleteDocument(payloadData)).then((res) => {
          if (res.error) {
            dispatch(
              openSnackbar({
                message: res?.payload?.response_error || ERRORS_MESSAGE.fetchErrorMsg,
                intent: "danger",
              }),
            );
          } else {
            dispatch(
              openSnackbar({
                message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.fileRemovedSuccess,
                intent: "success",
              }),
            );
          }
        });
      }
      setIsConfirmModalOpen(false);
    },
  };

  return (
    <Stack id={`document-${document?._id}`}>
      <Link
        href={isDisable ? null : document.url}
        size="small"
        target="_blank"
        rel="noopener noreferrer"
        color="secondary"
        style={{ textDecoration: "none" }}
      >
        <Stack direction="column" style={{ alignItems: "center", position: "relative" }}>
          <Card
            elevation={1}
            item
            xs={3}
            style={{
              height: "100px",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              // background: "transparent",
              padding: 5,
              objectFit: "cover",
              position: "relative",
            }}
          >
            <CardActionArea>
              <ImageComponent doc={document} />
            </CardActionArea>
            {/* Delete Icon */}
            <IconButton
              aria-label="delete"
              size="small"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                color: colors.validationError,
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsConfirmModalOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Card>
          <Typography variant="subtitle2" textAlign="center">
            {document?.name}
          </Typography>
        </Stack>
      </Link>
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        handle={handle.onDelete}
        setIsOpen={setIsConfirmModalOpen}
        title={"Remove document"}
        btnTextYes="Remove"
        message={"Are you sure you want to remove document?"}
        isConfirmLoading={isDeletingDocument}
        confirmLoadingText="Removing..."
      />
    </Stack>
  );
}

const ImageComponent = ({ width, doc }) => {
  const getDocIcon = (docExtension) => {
    switch (docExtension) {
      case ".html":
        return <HtmlTwoToneIcon fontSize="large" />;
      case ".csv":
        return <DescriptionIcon />;
      case ".pdf":
        return <PictureAsPdfTwoToneIcon />;

      default:
        return <PictureAsPdfTwoToneIcon />;
    }
  };

  if (doc?.ext === ".webm") {
    return (
      <video
        src={doc?.url}
        style={{
          minWidth: "76px",
          maxWidth: "76px",
        }}
      ></video>
    );
  }

  if (ext.includes(doc?.ext))
    return (
      <Paper
        elevation={0}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "5px",
          background: `url(${doc?.url})`,
          backgroundPosition: "center center",
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          padding: "5px",
          minHeight: "86px",
          minWidth: "76px",
          maxWidth: "76px",
        }}
      ></Paper>
    );

  return (
    <Paper
      elevation={0}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "5px",
        background: "transparent",
        padding: "30px 25px",
        minWidth: "76px",
        maxWidth: "76px",
      }}
    >
      {getDocIcon(doc?.ext)}
    </Paper>
  );
};
